import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaColumns, FaUsers } from "react-icons/fa";
import { GoPencil } from "react-icons/go";
import axios from "axios";
import ModifyColumns from "../Tools/ModifyColumns";
import {
  AddTableFilterComponent,
  apiRequestBodyBuilder,
  getSortedDataGLOBAL,
} from "./../config/tableHelper";
import { BiHide, BiSolidBulb } from "react-icons/bi";
import { RenderTable } from "../config/tableWrapper";
import { AGE_ADDTABLE_COLUMNS } from "../Components/AddTableCols";

// Icon color cycle for metric rows
const ICON_COLORS = [
  { bg: "bg-indigo-100", text: "text-indigo-600" },
  { bg: "bg-blue-100", text: "text-blue-600" },
  { bg: "bg-emerald-100", text: "text-emerald-600" },
  { bg: "bg-amber-100", text: "text-amber-600" },
  { bg: "bg-purple-100", text: "text-purple-600" },
  { bg: "bg-rose-100", text: "text-rose-600" },
];

const formatAgeRange = (range) => {
  const mapping = {
    AGE_RANGE_18_24: "18–24",
    AGE_RANGE_25_34: "25–34",
    AGE_RANGE_35_44: "35–44",
    AGE_RANGE_45_54: "45–54",
    AGE_RANGE_55_64: "55–64",
    AGE_RANGE_65_UP: "65+",
    AGE_RANGE_UNDETERMINED: "Undetermined",
  };
  return mapping[range] || range;
};

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Insights Skeleton consistent with PaidAdsPage aesthetic
function InsightsSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm animate-pulse">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0"
        >
          <div className="flex items-center gap-3">
            <div className="h-7 w-7 rounded-md bg-gray-200 shrink-0" />
            <div className="space-y-1.5">
              <div className="h-4 w-28 bg-gray-200 rounded" />
              <div
                className="h-3 bg-gray-100 rounded hidden sm:block"
                style={{ width: `${180 + (item % 2) * 60}px` }}
              />
            </div>
          </div>
          <div className="flex items-center gap-6 sm:gap-10">
            <div className="h-4 w-16 bg-gray-100 rounded hidden sm:block" />
            <div className="h-4 w-16 bg-gray-100 rounded" />
            <div className="h-4 w-20 bg-gray-100 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

const Audience = ({
  startDate,
  endDate,
  initialDateRange,
  insightAvailable = false,
  PageType,
}) => {
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState(AGE_ADDTABLE_COLUMNS);
  const [groups, setGroups] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [campaignNameInput, setCampaignNameInput] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [insightsArray, setInsightsArray] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [isEditingInsight, setIsEditingInsight] = useState(false);
  const [editedInsights, setEditedInsights] = useState([]);
  const [currentGroupType, setCurrencyGroupType] = useState("");

  // Configurable state variable for the 3 metrics shown in insight rows
  const [insightMetrics, setInsightMetrics] = useState([
    { key: "spend", label: "Spend" },
    { key: "conversions", label: "Conversions" },
    { key: "clicks", label: "Clicks" },
  ]);

  const filterRef = useRef(null);
  const columnRef = useRef(null);

  const fetchData = async (nameContains = "") => {
    setLoading(true);
    setUnfilteredData([]);
    let requestBody = apiRequestBodyBuilder(initialDateRange, {
      reporting_type: PageType,
    });
    if (nameContains) {
      requestBody.name_contains = nameContains;
    }
    const endpoint = nameContains
      ? "https://api.confidanto.com/audience/filter"
      : "https://api.confidanto.com/audience/data";
    try {
      const response = await axios.post(endpoint, requestBody);
      const rawData = response?.data;
      if (Array.isArray(rawData)) {
        const transformedData = rawData.map((item) => ({
          ...item,
          age_range: formatAgeRange(item.age_range),
          campaign_name: item.campaign_name ?? "–",
        }));
        setUnfilteredData(transformedData);
        setColumns(AGE_ADDTABLE_COLUMNS);
      } else {
        setUnfilteredData([]);
      }
    } catch (err) {
      setUnfilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = useCallback(async () => {
    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange);
      const responseInfo = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      if (!responseInfo.ok) {
        throw new Error(
          `Failed to fetch group info. Status: ${responseInfo.status}`,
        );
      }
      const groupType = await responseInfo.json();
      const typeForQuery =
        groupType?.type === "keyword"
          ? "keywords"
          : (groupType?.type ?? "campaign");
      const TYPEQUERY =
        typeForQuery?.charAt(0).toUpperCase() + typeForQuery?.slice(1);
      setCurrencyGroupType(TYPEQUERY);
      requestBody.type = typeForQuery;
      const response = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch groups. Status: ${response.status}`);
      }
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (err) {
      console.error(err);
      setGroups([]);
    }
  }, [initialDateRange]);

  const handleApplyCampaignFilter = () => {
    fetchData(campaignNameInput);
    setShowFilterDropdown(false);
    setSelectedFilter(null);
    const newFilter = {
      id: Date.now(),
      type: "campaign",
      value: campaignNameInput,
      label: `Campaign: ${campaignNameInput}`,
    };
    setAppliedFilters([newFilter]);
  };

  const handleGroupApply = async (remainingGroups = null) => {
    setLoading(true);
    setUnfilteredData([]);
    setShowFilterDropdown(false);
    setSelectedFilter(null);
    const activeGroups =
      remainingGroups !== null ? remainingGroups : selectedGroups;
    const selectedGroupIds = groups
      .filter((g) => activeGroups.includes(g.group_name))
      .map((g) => g.group_id);
    if (activeGroups.length === 0) {
      alert("Please select at least one group.");
      setLoading(false);
      return;
    }
    const groupFilters = activeGroups.map((group) => ({
      id: group,
      type: "groupwise",
      value: group,
      label: group,
    }));
    setAppliedFilters(groupFilters);
    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange, {
        group_ids: selectedGroupIds,
      });
      const response = await axios.post(
        "https://api.confidanto.com/audience-group/datewise-data",
        requestBody,
      );
      const responseData = response.data;
      if (responseData.length > 0) {
        const currency = responseData[0]?.group_totals?.currency;
        setCurrencySymbol(currency === "USD" ? "$" : "₹");
      }
      setUnfilteredData(responseData);
      setColumns(AGE_ADDTABLE_COLUMNS);
      setColumns((prevColumns) =>
        prevColumns.map((col) =>
          col.key === "age_range"
            ? {
                ...col,
                title: `${currentGroupType} Group`,
                key: "group_name",
                visible: true,
              }
            : col,
        ),
      );
    } catch (error) {
      setUnfilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyStaticFilter = (filter) => {
    setAppliedFilters((prev) => [...prev, filter]);
    setShowFilterDropdown(false);
  };

  const handleRemoveFilter = async (filterToRemove) => {
    const newFilters = appliedFilters.filter((f) => f.id !== filterToRemove.id);
    if (filterToRemove.type === "groupwise") {
      const remainingGroups = newFilters
        .filter((f) => f.type === "groupwise")
        .map((f) => f.value);
      setSelectedGroups(remainingGroups);
      if (remainingGroups.length === 0) {
        setAppliedFilters([]);
        fetchData();
      } else {
        handleGroupApply(remainingGroups);
      }
    } else {
      setAppliedFilters(newFilters);
      if (filterToRemove.type === "campaign" && newFilters.length === 0) {
        fetchData();
      }
    }
  };

  const getFilteredData = () => {
    const staticFilters = appliedFilters.filter((f) => f.type === "static");
    if (staticFilters.length === 0) return unfilteredData;
    return unfilteredData.filter((row) => {
      return staticFilters.every((filter) => {
        const rowValue =
          typeof row[filter.key] === "object"
            ? row[filter.key].value
            : row[filter.key];
        if (rowValue === null || rowValue === undefined) return false;
        if (filter.keyType === "number") {
          const rowNum = parseFloat(rowValue);
          const filterNum = parseFloat(filter.value);
          if (isNaN(rowNum) || isNaN(filterNum)) return false;
          switch (filter.comparison) {
            case ">":
              return rowNum > filterNum;
            case "<":
              return rowNum < filterNum;
            case "=":
              return rowNum === filterNum;
            default:
              return true;
          }
        } else if (filter.keyType === "boolean") {
          return filter.value.includes(String(rowValue));
        } else {
          return rowValue
            .toString()
            .toLowerCase()
            .includes(filter.value.toLowerCase());
        }
      });
    });
  };

  const filteredData = getFilteredData();
  const sortedData = getSortedDataGLOBAL(sortConfig, filteredData);

  // Helper function to find table row matching entity_name
  const findDataRowForEntity = (entityName) => {
    if (!entityName) return null;
    const cleanEntity = String(entityName).trim().toLowerCase();
    return (
      filteredData.find((row) => {
        const age = String(row.age_range || "")
          .trim()
          .toLowerCase();
        const group = String(row.group_name || "")
          .trim()
          .toLowerCase();
        const name = String(row.campaign_name || "")
          .trim()
          .toLowerCase();
        return (
          age === cleanEntity || group === cleanEntity || name === cleanEntity
        );
      }) || null
    );
  };

  // Helper function to extract and format metric values
  const getMetricValueFromData = (row, key) => {
    if (!row) return "–";

    let val = row[key];
    if (val === undefined || val === null) {
      if (key === "spend") val = row.cost ?? row.spend;
      if (key === "conversions") val = row.purchases ?? row.conversions;
      if (key === "roas") val = row.purchase_roas ?? row.roas;
    }

    if (val === undefined || val === null) return "–";

    if (typeof val === "object") {
      if (val.formatted !== undefined) return val.formatted;
      if (val.value !== undefined) val = val.value;
    }

    if (typeof val === "number") {
      if (
        key.toLowerCase().includes("spend") ||
        key.toLowerCase().includes("cost")
      ) {
        return `${currencySymbol}${val.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      }
      if (
        key.toLowerCase().includes("roas") ||
        key.toLowerCase().includes("ctr")
      ) {
        return Number(val).toFixed(2);
      }
      return val.toLocaleString("en-IN");
    }

    return String(val);
  };

  useEffect(() => {
    fetchData();
    fetchGroups();
    setAppliedFilters([]);
    setInsightsArray([]);
    setShowInsights(false);
    setIsEditingInsight(false);
    setCurrencySymbol(
      localStorage.getItem("customer_id") === "7948821642" ? "$" : "₹",
    );

    // Reset local storage for this component on date range change
    try {
      const currentStored = JSON.parse(
        localStorage.getItem("customrep_insight") || "{}",
      );
      currentStored["audience"] = false;
      localStorage.setItem("customrep_insight", JSON.stringify(currentStored));
    } catch (e) {
      console.error(e);
    }
  }, [
    initialDateRange[0].startDate,
    initialDateRange[0].endDate,
    initialDateRange.length,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideMuiMenu = event.target.closest(
        ".groupwise-select-menu",
      );
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        !isClickInsideMuiMenu
      ) {
        setShowFilterDropdown(false);
        setSelectedFilter(null);
      }
      if (columnRef.current && !columnRef.current.contains(event.target)) {
        setShowColumnsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchInsights = async () => {
    setInsightsLoading(true);
    setInsightsArray([]);
    try {
      const requestBody = apiRequestBodyBuilder(initialDateRange, {
        insight_type: "audience",
      });
      const response = await axios.post(
        "https://api.confidanto.com/get-insights/custom-reporting-insights",
        requestBody,
      );
      const data = response?.data;
      if (Array.isArray(data)) {
        setInsightsArray(data);
      } else if (Array.isArray(data?.data)) {
        setInsightsArray(data.data);
      } else if (Array.isArray(data?.insights)) {
        setInsightsArray(data.insights);
      } else {
        setInsightsArray([]);
      }
    } catch (err) {
      console.error("Failed to fetch insights:", err);
      setInsightsArray([]);
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleToggleInsights = () => {
    if (!showInsights && insightsArray.length === 0) {
      fetchInsights();
    }
    const newShowState = !showInsights;
    setShowInsights(newShowState);

    // Save toggle state to localStorage for Custom Reports
    try {
      const currentStored = JSON.parse(
        localStorage.getItem("customrep_insight") || "{}",
      );
      currentStored["audience"] = newShowState;
      localStorage.setItem("customrep_insight", JSON.stringify(currentStored));
    } catch (e) {
      console.error("Failed to save insight state", e);
    }
  };

  const updateInsight = async (newInsights) => {
    const baseRequestInfo = apiRequestBodyBuilder(initialDateRange, {});
    const updatePromises = newInsights.map((insight) => {
      const requestBody = {
        id: insight.id,
        ad_account_id: insight.ad_account_id || baseRequestInfo.ad_account_id,
        insight_type: insight.insight_type || "audience",
        entity_name: insight.entity_name,
        insight_text: insight.insight_text,
      };
      return axios.post(
        "https://api.confidanto.com/get-insights/update-custom-reporting-insights",
        requestBody,
      );
    });
    await Promise.all(updatePromises);
  };

  const handleEditClick = () => {
    setEditedInsights(insightsArray.map((insight) => ({ ...insight })));
    setIsEditingInsight(true);
  };

  const handleInsightChange = (index, newValue) => {
    const newArr = [...editedInsights];
    newArr[index] = { ...newArr[index], insight_text: newValue };
    setEditedInsights(newArr);
  };

  const handleSaveInsight = async () => {
    setInsightsLoading(true);
    try {
      await updateInsight(editedInsights);
      setInsightsArray(editedInsights);
      setIsEditingInsight(false);
    } catch (error) {
      console.error("Failed to save insight:", error);
      alert("Failed to save some insights.");
    } finally {
      setInsightsLoading(false);
    }
  };

  const activeInsightsData = isEditingInsight ? editedInsights : insightsArray;

  return (
    <div className="flex h-full bg-gray-100 font-roboto">
      <main className="w-full p-4">
        <div className="flex justify-between items-center mb-3 space-x-1.5">
          <div
            className={`transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
          >
            <AddTableFilterComponent
              data={unfilteredData}
              onApplyStaticFilter={handleApplyStaticFilter}
              filterRef={filterRef}
              showFilterDropdown={showFilterDropdown}
              setShowFilterDropdown={setShowFilterDropdown}
              appliedFilters={appliedFilters}
              handleRemoveFilter={handleRemoveFilter}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              onFetchGroups={fetchGroups}
              columns={columns}
              campaignNameInput={campaignNameInput}
              setCampaignNameInput={setCampaignNameInput}
              onApplyCampaignFilter={handleApplyCampaignFilter}
              groups={groups}
              selectedGroups={selectedGroups}
              setSelectedGroups={setSelectedGroups}
              onApplyGroupFilter={handleGroupApply}
              currentGroupType={currentGroupType}
            />
          </div>
          <div className="flex">
            <div className="relative" ref={columnRef}>
              <button
                disabled={loading}
                className={`bg-transparent text-gray-600 px-3 py-1.5 rounded flex flex-col items-center justify-center transition-all ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                onClick={() => setShowColumnsMenu(!showColumnsMenu)}
              >
                <FaColumns className="text-sm" />
                <span className="text-sm">Columns</span>
              </button>
              {showColumnsMenu && (
                <ModifyColumns
                  columns={columns}
                  setColumns={setColumns}
                  setShowColumnsMenu={setShowColumnsMenu}
                  colsLimit={13}
                />
              )}
            </div>
          </div>
        </div>

        {/* Table & Loading State */}
        {loading ? (
          <TableSkeleton />
        ) : (
          <div className="overflow-x-auto bg-white rounded shadow">
            {filteredData.length > 0 ? (
              <RenderTable
                columns={columns}
                data={filteredData}
                enableExpandCollapse={initialDateRange.length > 1}
              />
            ) : (
              <div className="flex justify-center items-center h-40 mt-6">
                <h2>No Data Found</h2>
              </div>
            )}
          </div>
        )}

        {insightAvailable && (
          <div className="w-full my-3">
            <button
              disabled={loading}
              onClick={handleToggleInsights}
              className={`flex items-center justify-center gap-1.5 w-auto bg-[#283593] text-white font-semibold py-1.5 px-3 rounded-lg shadow-md transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-opacity-75 ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#1a237e]"}`}
            >
              {showInsights ? (
                <>
                  <BiHide size="1.2em" />
                  <span className="text-sm">Hide Insights</span>
                </>
              ) : (
                <>
                  <BiSolidBulb size="1.2em" />
                  <span className="text-sm">Show Insights</span>
                </>
              )}
            </button>
            <div
              className={`w-full transition-all duration-500 ease-in-out overflow-hidden ${showInsights ? "opacity-100 mt-2" : "max-h-0 opacity-0"}`}
            >
              {insightsLoading ? (
                <InsightsSkeleton />
              ) : (
                <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-lg shadow-sm w-full relative group">
                  {!isEditingInsight && insightsArray.length > 0 && (
                    <button
                      onClick={handleEditClick}
                      className="absolute top-3 right-3 bg-blue-600 text-white p-2 rounded-full shadow-md hover:bg-blue-700 transition-transform hover:scale-105 z-10"
                    >
                      <GoPencil size={16} />
                    </button>
                  )}
                  {insightsArray.length > 0 ? (
                    <div className="space-y-2 pr-8">
                      {activeInsightsData.map((item, index) => {
                        const matchedRow = findDataRowForEntity(
                          item.entity_name,
                        );
                        const iconColor =
                          ICON_COLORS[index % ICON_COLORS.length];

                        return (
                          <div
                            key={index}
                            className="py-1.5 border-b border-slate-200/80 last:border-b-0 transition-colors"
                          >
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                              {/* Left: Audience Icon, Entity Title & Full Insight Text */}
                              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                                <div
                                  className={`p-1.5 rounded-md shrink-0 mt-0.5 ${iconColor.bg}`}
                                >
                                  <FaUsers
                                    className={iconColor.text}
                                    size={15}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-xs md:text-sm text-[#283593] whitespace-nowrap">
                                      {item.entity_name}
                                    </span>
                                  </div>
                                  {!isEditingInsight && (
                                    <p className="text-slate-600 text-xs md:text-sm leading-snug mt-0.5 break-words">
                                      {item.insight_text}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Right: Clean inline metrics */}
                              <div className="flex items-center gap-2 shrink-0 self-end md:self-center pl-7 md:pl-0">
                                <div className="flex items-center gap-1.5 text-xs text-slate-600">
                                  {insightMetrics.map((metric, mIdx) => {
                                    const val = getMetricValueFromData(
                                      matchedRow,
                                      metric.key,
                                    );
                                    return (
                                      <React.Fragment key={metric.key}>
                                        {mIdx > 0 && (
                                          <span className="text-slate-400 font-bold mx-0.5">
                                            •
                                          </span>
                                        )}
                                        <span className="whitespace-nowrap">
                                          <span className="text-slate-500 font-normal">
                                            {metric.label}:{" "}
                                          </span>
                                          <span className="font-semibold text-slate-800">
                                            {val}
                                          </span>
                                        </span>
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* Editing Textarea mode */}
                            {isEditingInsight && (
                              <div className="mt-2 w-full">
                                <textarea
                                  className="w-full resize-y bg-white border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 rounded-md p-2 text-xs md:text-sm text-slate-700 outline-none transition-all"
                                  value={item.insight_text}
                                  onChange={(e) =>
                                    handleInsightChange(index, e.target.value)
                                  }
                                  rows={2}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-red-600 mt-2">
                      No Insights found for above data
                    </p>
                  )}
                  {isEditingInsight && (
                    <div className="flex justify-end gap-3 mt-4 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => setIsEditingInsight(false)}
                        className="px-4 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveInsight}
                        className="px-4 py-1.5 text-xs font-medium text-white bg-[#283593] rounded-lg shadow-sm hover:bg-[#1a237e] transition-all"
                      >
                        Save Changes
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Audience;
